import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CContainer, 
  CRow, 
  CCol, 
  CCard, 
  CCardBody, 
  CSpinner, 
  CBadge,
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter 
} from '@coreui/react';
import api from '../../api/axiosInstance';
import { Lock, Unlock, List, BookOpen, ChevronRight, FileText } from 'lucide-react';

const BookSolution = () => {
  const { categoryTitle } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cTitle, setCTitle] = useState();

  // Modal State
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '', type: 'auth' });

  // Auth & Subscription Details
  const authToken = localStorage.getItem('authToken');
  const userTier = localStorage.getItem('subscriptionTier') || 'Alpha';
  const isSubActive = localStorage.getItem('isSubscriptionActive') === 'true';

  // Allowed Tiers Hierarchy for Access
  const allowedTiers = ['Gamma'];
  const hasGammaAccess = authToken && isSubActive && allowedTiers.includes(userTier);
  const allowedDeltaTiers = ['Delta'];
  const hasDeltaAccess = authToken && isSubActive && allowedDeltaTiers.includes(userTier);

  useEffect(() => {
    const fetchSolutionData = async () => {
      try {
        setLoading(true);
        setError(null);

        const formattedTitle = categoryTitle ? categoryTitle.replace(/-/g, ' ') : 'Olympiad Math Pathfinder';
        const endpoint = `/api/content/GetBookSolutionData?categoryTitle=${encodeURIComponent(formattedTitle)}`;
        const safeTitle = categoryTitle || 'Olympiad-Math-Pathfinder';
        const cTitle = safeTitle.trim().replace(/\s+/g, '-').toLocaleLowerCase();
        setCTitle(cTitle);

        const res = await api.get(endpoint, { skipAuthCheck: true });
        setData(res.data);
      } catch (err) {
        console.error("Error fetching book solution data:", err);
        setError("Failed to load content for this course/book.");
      } finally {
        setLoading(false);
      }
    };

    fetchSolutionData();
  }, [categoryTitle]);

  // Click handler for Topics and Exercises with Gamma Access Check
  const handleItemClick = (e, targetUrl) => {
    e.preventDefault();

    // 1. LOGIN CHECK
    if (!authToken) {
      setModalConfig({
        title: 'Authentication Required 🔒',
        message: 'Content aur solutions dekhne ke liye Login zaroori hai.',
        type: 'auth'
      });
      setModalVisible(true);
      return;
    }

    // 2. GAMMA SUBSCRIPTION CHECK
    if (!hasGammaAccess) {
      setModalConfig({
        title: 'Gamma Subscription Required 👑',
        message: 'To unlock this content, you must have an active subscription plan of Gamma.',
        type: 'upgrade'
      });
      setModalVisible(true);
      return;
    }

    // Access Granted
    if (targetUrl) {
      navigate(targetUrl);
    }
  };

  const handleModalConfirm = () => {
    setModalVisible(false);
    if (modalConfig.type === 'auth') {
      navigate('/login', { state: { from: location.pathname } });
    } else {
      navigate('/profile');
    }
  };

  if (loading) {
    return (
      <CContainer className="text-center py-5 my-5">
        <CSpinner color="primary" size="lg" />
        <p className="mt-3 text-dark fw-bold">Loading Solutions & Notes...</p>
      </CContainer>
    );
  }

  if (error || !data) {
    return (
      <CContainer className="py-5 text-center">
        <div className="alert alert-danger shadow-sm border-0 rounded-3 py-3 fw-semibold">
          {error || "No data available."}
        </div>
      </CContainer>
    );
  }

  const chaptersList = data.chapters || data.Chapters || [];

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      {/* 🌟 FULL-WIDTH EDGE-TO-EDGE BLUE BANNER */}
      <div 
        className="text-white text-center py-5 shadow-sm mb-5" 
        style={{ 
          backgroundColor: 'rgb(0, 33, 71)',
          width: '100vw',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <span className="text-warning fw-bold text-uppercase" style={{ fontSize: '13px', letterSpacing: '1px' }}>
            CHAPTERWISE SOLUTIONS
          </span>
          <h1 className="fw-bold m-0   mt-1 display-6 text-capitalize" style={{ color: 'yellow' }}>
            {data.categoryTitle} Notes & Solutions
          </h1>
          <p className="text-light opacity-90 m-0 small mt-2">
            Complete step-by-step topic breakdown and study material
          </p>
        </div>
      </div>

      {/* 🚀 MAIN CONTENT CONTAINER */}
      <CContainer className="pb-5" style={{ maxWidth: '1140px' }}>
        
        {/* Intro Content Banner */}
        {data.introContent && (
          <div 
            className="p-4 mb-5 rounded-4 bg-white shadow-sm border-start border-4 border-primary"
            style={{ border: '1px solid #cbd5e1' }}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <BookOpen size={20} className="text-primary" />
              <h5 className="fw-bold text-dark m-0">{data.noteTitle || 'Course Overview'}</h5>
            </div>
            <div 
              className="text-dark ck-content fw-medium"
              style={{ lineHeight: '1.7', fontSize: '0.975rem', color: '#334155' }}
              dangerouslySetInnerHTML={{ __html: data.introContent }}
            />
          </div>
        )}

        <CRow className="g-4">
          
          {/* Table of Contents Sidebar */}
          <CCol lg={4} md={5}>
            <CCard className="shadow-sm border rounded-4 sticky-top" style={{ top: '90px', borderColor: '#cbd5e1' }}>
              <CCardBody className="p-4">
                <div className="d-flex align-items-center gap-2 border-bottom pb-3 mb-3" style={{ borderColor: '#e2e8f0' }}>
                  <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                    <List size={20} className="text-primary" />
                  </div>
                  <h5 className="fw-bold text-dark m-0">Table of Contents</h5>
                </div>

                <div className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.8px', color: '#64748b' }}>
                  {data.categoryTitle}
                </div>

                <div className="d-flex flex-column gap-1">
                  {chaptersList.map((chap, idx) => (
                    <a 
                      key={chap.chapterId || idx}
                      href={`#chapter-${chap.chapterId}`} 
                      className="sidebar-chapter-link p-2 px-3 rounded-3 text-decoration-none fw-bold d-flex align-items-center justify-content-between"
                      style={{ color: '#1e293b' }}
                    >
                      <span>Chapter {idx + 1}: {chap.bookChapterName}</span>
                      <ChevronRight size={16} className="arrow-icon opacity-75" />
                    </a>
                  ))}
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          {/* Main Chapters Content Area */}
          <CCol lg={8} md={7}>
            {chaptersList.length > 0 ? (
              chaptersList.map((chapter, idx) => (
                <div 
                  key={chapter.chapterId} 
                  id={`chapter-${chapter.chapterId}`} 
                  className="mb-5 p-4 bg-white rounded-4 shadow-sm border scroll-margin-top"
                  style={{ borderColor: '#cbd5e1' }}
                >
                  {/* Chapter Heading */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="badge bg-primary rounded-pill px-3 py-2 fw-bold fs-6">
                      Chapter {idx + 1}
                    </span>
                    <h3 className="fw-bold text-dark m-0 fs-4" style={{ color: '#0f172a' }}>
                      {chapter.bookChapterName}
                    </h3>
                  </div>

                  {/* Chapter Short Description */}
                  {(chapter.chapterShort || chapter.shortDescription) && (
                    <div 
                      className="p-3 mb-4 rounded-3 border-start border-4 border-info"
                      style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1' }}
                    >
                      <div 
                        className="ck-content fw-medium"
                        style={{ color: '#334155', fontSize: '0.95rem' }}
                        dangerouslySetInnerHTML={{ __html: chapter.chapterShort || chapter.shortDescription }}
                      />
                    </div>
                  )}

                  {/* Notes / Topics Section */}
                  <div className="d-flex flex-column gap-2 mt-3">
                    {chapter.topics?.map((topic) => {
                      const isLocked = !hasDeltaAccess;

                      return (
                        <div
                          key={`topic-${topic.id}`}
                          className="topic-item-card d-flex align-items-center justify-content-between p-3 rounded-3 cursor-pointer transition-all bg-white text-dark border shadow-xs hover-shadow"
                          style={{ borderColor: '#cbd5e1' }}
                          onClick={(e) => {
                            if (isLocked) {
                              e.preventDefault();
                              setModalConfig({
                                title: 'Delta Subscription Required 👑',
                                message: 'A Delta or Sigma plan is required to access this content.',
                                type: 'upgrade'
                              });
                              setModalVisible(true);
                              return;
                            }

                            handleItemClick(e, `/topic-questions/${topic.id}`);
                          }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            {isLocked ? (
                              <div className="p-2 bg-light border rounded-circle">
                                <Lock size={16} className="text-secondary" />
                              </div>
                            ) : (
                              <div className="p-2 bg-success bg-opacity-10 rounded-circle">
                                <Unlock size={16} className="text-success" />
                              </div>
                            )}
                            <span className="fw-bold fs-6" style={{ color: '#0f172a' }}>
                              Notes : {topic.name}
                            </span>
                          </div>

                          {isLocked ? (
                            <CBadge color="secondary" className="px-3 py-1.5 rounded-pill text-dark border bg-light fw-bold">
                              Locked (Delta Req.)
                            </CBadge>
                          ) : (
                            <span className="text-primary fw-bold small d-flex align-items-center gap-1">
                              View Solution <ChevronRight size={16} />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Exercises Section */}
                  {chapter.exercises && chapter.exercises.length > 0 && (
                    <div className="d-flex flex-column gap-2 mt-2">
                      {chapter.exercises.map((ex) => {
                        const isLocked = !hasGammaAccess;

                        return (
                          <div
                            key={`exercise-${ex.exerciseNo}`}
                            className="topic-item-card d-flex align-items-center justify-content-between p-3 rounded-3 cursor-pointer transition-all bg-white text-dark border shadow-xs hover-shadow"
                            style={{ borderColor: '#cbd5e1' }}
                            onClick={(e) => 
                              handleItemClick(
                                e, 
                                `/${encodeURIComponent(cTitle)}/${chapter.chapterName.trim().replace(/\s+/g, '-').toLocaleLowerCase()}/exercise-questions/${ex.exerciseNo}`
                              )
                            }
                          >
                            <div className="d-flex align-items-center gap-3">
                              {isLocked ? (
                                <div className="p-2 bg-light border rounded-circle">
                                  <Lock size={16} className="text-secondary" />
                                </div>
                              ) : (
                                <div className="p-2 bg-primary bg-opacity-10 rounded-circle">
                                  <FileText size={16} className="text-primary" />
                                </div>
                              )}
                              <span className="fw-bold fs-6" style={{ color: '#0f172a' }}>
                                {ex.exerciseName || `Exercise ${ex.exerciseNo}`}
                              </span>
                            </div>

                            {isLocked ? (
                              <CBadge color="secondary" className="px-3 py-1.5 rounded-pill text-dark border bg-light fw-bold">
                                Locked (Gamma Req.)
                              </CBadge>
                            ) : (
                              <span className="text-primary fw-bold small d-flex align-items-center gap-1">
                                Practice Questions <ChevronRight size={16} />
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                </div>
              ))
            ) : (
              <div className="alert alert-info text-center rounded-4 border p-4 shadow-sm fw-bold">
                No chapters found for this book category.
              </div>
            )}
          </CCol>

        </CRow>

        {/* COREUI MODAL FOR SUBSCRIPTION CHECK */}
        <CModal visible={modalVisible} onClose={() => setModalVisible(false)} alignment="center">
          <CModalHeader className="bg-dark text-white">
            <CModalTitle className="fw-bold fs-5">{modalConfig.title}</CModalTitle>
          </CModalHeader>
          <CModalBody className="py-4">
            <p className="fs-6 text-dark m-0">{modalConfig.message}</p>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setModalVisible(false)}>
              Close
            </CButton>
            <CButton color={modalConfig.type === 'upgrade' ? "success" : "primary"} className="text-white fw-bold" onClick={handleModalConfirm}>
              {modalConfig.type === 'upgrade' ? "Upgrade Plan Now" : "Login Now"}
            </CButton>
          </CModalFooter>
        </CModal>

        {/* Embedded High-Contrast Styling */}
        <style>{`
          .scroll-margin-top {
            scroll-margin-top: 100px;
          }
          .cursor-pointer {
            cursor: pointer;
          }
          .sidebar-chapter-link {
            transition: all 0.2s ease;
            font-size: 0.95rem;
          }
          .sidebar-chapter-link:hover {
            background-color: #e2e8f0;
            color: #0d6efd !important;
          }
          .sidebar-chapter-link:hover .arrow-icon {
            opacity: 1 !important;
            transform: translateX(3px);
            transition: transform 0.2s ease;
          }
          .topic-item-card {
            border: 1px solid #cbd5e1 !important;
            transition: all 0.2s ease-in-out;
          }
          .topic-item-card:hover {
            border-color: #0d6efd !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(13, 110, 253, 0.12) !important;
          }
        `}</style>
      </CContainer>
    </div>
  );
};

export default BookSolution;